// ============================================================================
// Patient Type Constants
// ============================================================================

// Single source of truth for patient type configuration
const PATIENT_TYPE_CONFIG = {
  rpm: {
    value: "rpm",
    label: "RPM (Remote Patient Monitoring)",
    description: "General health status monitoring and data collection",
  },
  rtm: {
    value: "rtm",
    label: "RTM (Remote Therapeutic Monitoring)",
    description: "Continuous health monitoring with therapeutic interventions",
  },
} as const;

// Derive all patient type constants from the config
export const PATIENT_TYPES = {
  RPM: PATIENT_TYPE_CONFIG.rpm.value,
  RTM: PATIENT_TYPE_CONFIG.rtm.value,
} as const;

export type PatientType = typeof PATIENT_TYPES[keyof typeof PATIENT_TYPES];

export const PATIENT_TYPE_LABELS: Record<PatientType, string> = {
  [PATIENT_TYPES.RPM]: PATIENT_TYPE_CONFIG.rpm.label,
  [PATIENT_TYPES.RTM]: PATIENT_TYPE_CONFIG.rtm.label,
};

export const PATIENT_TYPE_DESCRIPTIONS: Record<PatientType, string> = {
  [PATIENT_TYPES.RPM]: PATIENT_TYPE_CONFIG.rpm.description,
  [PATIENT_TYPES.RTM]: PATIENT_TYPE_CONFIG.rtm.description,
};

export const PATIENT_TYPE_OPTIONS = [
  PATIENT_TYPE_CONFIG.rpm,
  PATIENT_TYPE_CONFIG.rtm,
];

// ============================================================================
// Consent Status Filter Constants
// ============================================================================

// Values for the `consentStatus` list/report filter (CCI consent). `signed`
// means the patient holds the current CCI version, and `outstanding` is its
// complement (never signed, an older version, or declined-and-not-re-signed).
// The API accepts exactly one value at a time, or no param for "all".
export const CONSENT_STATUS_OPTIONS = [
  { value: "signed", label: "Signed" },
  { value: "outstanding", label: "Outstanding" },
] as const

export const CONSENT_STATUS_VALUES = CONSENT_STATUS_OPTIONS.map(o => o.value) as [
  "signed",
  "outstanding",
]

export type ConsentStatusValue = (typeof CONSENT_STATUS_OPTIONS)[number]["value"]

// ============================================================================
// Device Constants
// ============================================================================

export type DeviceOption = {
  value: string;
  label: string;
};

// Base device definitions
const BASE_DEVICES: DeviceOption[] = [
  { value: "5", label: "Apple Health Kit" },
  { value: "4", label: "Smart Ring" },
  { value: "7", label: "Google Connect" },
];

const MANUAL_ENTRY: DeviceOption = { value: "6", label: "Manual Entry" };

// Derived device options for different use cases
export const DEVICE_OPTIONS = BASE_DEVICES;

export const DEVICE_OPTIONS_WITH_MANUAL = [...BASE_DEVICES, MANUAL_ENTRY];

// ============================================================================
// Sleep Stage Constants
// ============================================================================

export const STAGE_COLORS: Record<"REM" | "Deep" | "Core" | "Awake" | "Unknown", string> = {
  REM: "#4F46E5",
  Deep: "#1E40AF",
  Core: "#0284C7",
  Awake: "#f59e0b",
  Unknown: "#6B7280",
};

// ============================================================================
// Timezone Constants
// ============================================================================

export const TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

// ============================================================================
// Apnea Assessment Constants
// ============================================================================

// Risk thresholds for apnea screening
export const APNEA_RISK_THRESHOLDS = {
  HIGH: 0.7,
  MODERATE: 0.4,
} as const

export const APNEA_WINDOW_RISK_THRESHOLDS = {
  HIGH: 70,
  MODERATE: 40,
} as const

// Chart styling for flagged windows
export const APNEA_FLAGGED_WINDOW_COLORS = {
  FILL: "rgba(249, 115, 22, 0.18)",
  STROKE: "rgba(194, 65, 12, 0.6)",
} as const
